import React, {
 useId, useCallback, useEffect, useRef, useMemo 
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { map } from './core/MapView';
import { getStatusColor } from '../common/util/formatter';
import { mapIconKey } from './core/preloadImages';
import { useAttributePreference } from '../common/util/preferences';
import { useCatchCallback } from '../reactHelper';
import { icons, iconsRemote } from './core/icons3d';
import { useMapControlSetting } from './controls/useMapControls';
import MapClusterPulse from './main/MapClusterPulse';

export const findFonts = (map) => {
  // Verificação de segurança para garantir que o mapa e getStyle existam
  if (!map || typeof map.getStyle !== 'function') {
    return ['Open Sans Bold', 'Arial Unicode MS Bold'];
  }

  try {
    const style = map.getStyle();
    // Verifica se o estilo e a propriedade glyphs existem
    if (!style || !style.glyphs || typeof style.glyphs !== 'string') {
      return ['Open Sans Regular', 'Arial Unicode MS Regular'];
    }

    // Verifica o prefixo da URL de glyphs
    if (style.glyphs.startsWith('https://tiles.openfreemap.org')) {
      return ['Noto Sans Bold'];
    }

    return ['Open Sans Regular', 'Arial Unicode MS Regular'];
  } catch (error) {
    console.error('Error getting map style:', error);
    return ['Open Sans Bold', 'Arial Unicode MS Regular'];
  }
};

const MapPositions = ({ positions, onMapClick, onMarkerClick, showStatus, selectedPosition, titleField }) => {
  const id = useId();
  const clusters = `${id}-clusters`;
  const selected = `${id}-selected`;

  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const iconScale = useAttributePreference('iconScale', desktop ? 0.75 : 1);

  // 2ECOMP: shallowEqual evita re-render quando devices não mudou estruturalmente
  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);

  const mapClusterPref = useAttributePreference('mapCluster', true);
  const directionType = useAttributePreference('mapDirection', 'selected');

  // Map controls panel settings - usando hooks individuais para melhor performance
  const showLabels = useMapControlSetting('showLabels', true);
  const showDevices = useMapControlSetting('showDevices', true);
  const clusteringSetting = useMapControlSetting('clustering', mapClusterPref);
  const mapCluster = clusteringSetting ?? mapClusterPref;

  // 2ECOMP OPTIMIZED: Cores resolvidas por expressão MapLibre (GPU)
  // Não precisa mais calcular labelColor em JS - MapLibre faz isso na GPU
  const createFeature = useCallback((devices, position, selectedPositionId) => {
    const device = devices[position.deviceId];
    // 2ECOMP: Garantir course é número válido (evita NaN no MapLibre)
    const rawCourse = position.course;
    const course = (typeof rawCourse === 'number' && !Number.isNaN(rawCourse)) ? rawCourse : 0;
    const category = mapIconKey(device.category);
    const hasCustomIcon = icons[category] || iconsRemote[category];

    // Calcula valores numéricos com proteção contra NaN
    const rotation = hasCustomIcon ? (course % 22.5) : 0;
    const iconDegrees = course - (course % 22.5);

    // Calcula cor do status uma vez (necessário para nome do ícone)
    const statusColorKey = showStatus
      ? (position.attributes?.color || getStatusColor(device.status, position.attributes?.motion) || 'neutral')
      : 'neutral';

    // Detecta se está em movimento (para cor do texto no mapa)
    const isMoving = showStatus && position.attributes?.motion === true;

    // Propriedades mínimas - labelColor removido (resolvido por expressão MapLibre)
    return {
      id: position.id ?? 0,
      deviceId: position.deviceId ?? 0,
      name: device.name || '',
      category: category || 'default',
      color: statusColorKey,
      // 2ECOMP: Flag de movimento para expressão MapLibre (texto azul)
      moving: isMoving ? 1 : 0,
      // 2ECOMP: Garantir valores numéricos nunca são null/NaN
      rotation: Number.isFinite(rotation) ? rotation : 0,
      iconDegrees: Number.isFinite(iconDegrees) ? iconDegrees : 0,
      // direction só calculado se necessário (evita cálculo para 'none')
      direction: directionType === 'none' ? false
        : directionType === 'all' ? course > 0
        : selectedPositionId === position.id && course > 0,
    };
  }, [directionType, showStatus]);

  // 2ECOMP: useCallback para estabilizar referências de handlers
  const onMouseEnter = useCallback(() => {
    map.getCanvas().style.cursor = 'pointer';
  }, []);

  const onMouseLeave = useCallback(() => {
    map.getCanvas().style.cursor = '';
  }, []);

  const onMapClickCallback = useCallback((event) => {
    if (!event.defaultPrevented && onMapClick) {
      onMapClick(event.lngLat.lat, event.lngLat.lng);
    }
  }, [onMapClick]);

  const onMarkerClickCallback = useCallback((event) => {
    event.preventDefault();
    const feature = event.features[0];
    if (onMarkerClick) {
      onMarkerClick(feature.properties.id, feature.properties.deviceId);
    }
  }, [onMarkerClick]);

  const onClusterClick = useCatchCallback(async (event) => {
    event.preventDefault();
    const features = map.queryRenderedFeatures(event.point, {
      layers: [clusters],
    });
    const clusterId = features[0].properties.cluster_id;
    const zoom = await map.getSource(id).getClusterExpansionZoom(clusterId);
    map.easeTo({
      center: features[0].geometry.coordinates,
      zoom,
    });
  }, [clusters]);

  useEffect(() => {
    map.addSource(id, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      cluster: mapCluster,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });
    map.addSource(selected, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    [id, selected].forEach((source) => {
      map.addLayer({
        id: source,
        type: 'symbol',
        source,
        filter: ['!has', 'point_count'],
        layout: {
          'icon-image': '{category}-{color}-{iconDegrees}',
          'icon-size': iconScale,
          'icon-allow-overlap': true,
          'text-field': `{${titleField || 'name'}}`,
          'text-allow-overlap': true,
          'text-anchor': 'bottom',
          'text-offset': [0, -2 * iconScale],
          'text-size': 14,
          // 2ECOMP: coalesce evita "Expected number, found null" do MapLibre
          'symbol-sort-key': ['coalesce', ['get', 'id'], 0],
          'text-font': findFonts(map),
          'icon-rotate': ['coalesce', ['get', 'rotation'], 0],
        },
        paint: {
          // 2ECOMP PREMIUM: Cores por status com glow para moving
          'text-color': [
            'case',
            ['==', ['get', 'moving'], 1], '#3b82f6',
            ['==', ['get', 'color'], 'success'], '#16a34a',
            ['==', ['get', 'color'], 'error'], '#ef4444',
            '#78909c'
          ],
          'text-halo-color': [
            'case',
            ['==', ['get', 'moving'], 1], 'rgba(59, 130, 246, 0.25)',
            'white'
          ],
          'text-halo-width': [
            'case',
            ['==', ['get', 'moving'], 1], 3,
            2
          ],
          'text-halo-blur': [
            'case',
            ['==', ['get', 'moving'], 1], 2,
            0
          ],
        },
      });

      map.on('mouseenter', source, onMouseEnter);
      map.on('mouseleave', source, onMouseLeave);
      map.on('click', source, onMarkerClickCallback);
    });
    // Cluster layer com visual estilo GPSWOX (anéis concêntricos)
    map.addLayer({
      id: clusters,
      type: 'symbol',
      source: id,
      filter: ['has', 'point_count'],
      layout: {
        // 2ECOMP: coalesce em todos os step() evita null warnings
        'icon-image': [
          'step',
          ['coalesce', ['get', 'point_count'], 1],
          'cluster-small',   // 1-4 devices
          5,
          'cluster-medium',  // 5-9 devices
          10,
          'cluster-large',   // 10-999 devices
          1000,
          'cluster-xlarge',  // 1000+ devices (4 dígitos: "1.1k")
        ],
        'icon-size': [
          'step',
          ['coalesce', ['get', 'point_count'], 1],
          iconScale,           // 1-9: tamanho base
          10,
          iconScale * 1.1,     // 10-99: +10%
          100,
          iconScale * 1.2,     // 100-999: +20% (3 dígitos)
          1000,
          iconScale * 1.0,     // 1000+: tamanho base (SVG xlarge já é maior)
        ],
        'icon-allow-overlap': true,
        'text-field': '{point_count_abbreviated}',
        'text-font': findFonts(map),
        'text-size': [
          'step',
          ['coalesce', ['get', 'point_count'], 1],
          12,    // 1-9: fonte pequena
          10,
          13,    // 10-99: fonte média
          100,
          12,    // 100-999: fonte menor (3 dígitos)
          1000,
          14,    // 1000+: fonte maior (usa "1.4k")
        ],
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.2)',
        'text-halo-width': 1,
      },
    });

    map.on('mouseenter', clusters, onMouseEnter);
    map.on('mouseleave', clusters, onMouseLeave);
    map.on('click', clusters, onClusterClick);
    map.on('click', onMapClickCallback);

    return () => {
      map.off('mouseenter', clusters, onMouseEnter);
      map.off('mouseleave', clusters, onMouseLeave);
      map.off('click', clusters, onClusterClick);
      map.off('click', onMapClickCallback);

      if (map.getLayer(clusters)) {
        map.removeLayer(clusters);
      }

      [id, selected].forEach((source) => {
        map.off('mouseenter', source, onMouseEnter);
        map.off('mouseleave', source, onMouseLeave);
        map.off('click', source, onMarkerClickCallback);

        if (map.getLayer(source)) {
          map.removeLayer(source);
        }
        if (map.getSource(source)) {
          map.removeSource(source);
        }
      });
    };
  }, [mapCluster, clusters, onMarkerClickCallback, onClusterClick, onMouseEnter, onMouseLeave, onMapClickCallback]);

  // 2ECOMP OPTIMIZED: Cache de features para reduzir alocações de memória
  // Reutiliza objetos Feature quando position não mudou
  const featureCacheRef = useRef(new Map());

  // Limpar cache no unmount para liberar memória
  useEffect(() => () => {
    featureCacheRef.current.clear();
  }, []);

  const mainFeatures = useMemo(() => {
    // 2ECOMP DEBUG: Contadores para rastrear filtragem
    let noDevice = 0;
    let invalidCoords = 0;
    let isSelected = 0;

    const selectedPosId = selectedPosition?.id;
    const cache = featureCacheRef.current;
    const result = [];

    positions.forEach((position) => {
      if (!devices[position.deviceId]) {
        noDevice++;
        return;
      }
      // 2ECOMP: Validação robusta - coordenadas devem ser números finitos
      if (!Number.isFinite(position.latitude) || !Number.isFinite(position.longitude)) {
        invalidCoords++;
        return;
      }
      if (position.deviceId === selectedDeviceId) {
        isSelected++;
        return;
      }

      const cacheKey = position.deviceId;
      const cached = cache.get(cacheKey);

      // Reutiliza feature se coordenadas não mudaram
      if (cached
        && cached.geometry.coordinates[0] === position.longitude
        && cached.geometry.coordinates[1] === position.latitude) {
        // Atualiza só properties (objeto menor)
        cached.properties = createFeature(devices, position, selectedPosId);
        result.push(cached);
      } else {
        // Cria novo feature
        const feature = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [position.longitude, position.latitude],
          },
          properties: createFeature(devices, position, selectedPosId),
        };
        cache.set(cacheKey, feature);
        result.push(feature);
      }
    });

    // 2ECOMP DEBUG: Log detalhado de filtragem
    // console.log(`[MapPositions] Total: ${positions.length}, Rendered: ${result.length}, Filtered: noDevice=${noDevice}, invalidCoords=${invalidCoords}, isSelected=${isSelected}`);

    return result;
  }, [positions, devices, selectedDeviceId, selectedPosition, createFeature]);

  const selectedFeatures = useMemo(() => {
    const selectedPosId = selectedPosition?.id;
    return positions.reduce((acc, position) => {
      if (!devices[position.deviceId]) return acc;
      // 2ECOMP: Validação robusta - coordenadas devem ser números finitos
      if (!Number.isFinite(position.latitude) || !Number.isFinite(position.longitude)) return acc;
      if (position.deviceId !== selectedDeviceId) return acc;

      acc.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [position.longitude, position.latitude],
        },
        properties: createFeature(devices, position, selectedPosId),
      });
      return acc;
    }, []);
  }, [positions, devices, selectedDeviceId, selectedPosition, createFeature]);

  // 2ECOMP: RAF para batching de atualizações do mapa (evita múltiplos setData por frame)
  const rafRef = useRef(null);
  const pendingUpdateRef = useRef({ main: null, selected: null });

  useEffect(() => {
    // Agenda atualização para próximo frame
    pendingUpdateRef.current.main = mainFeatures;
    pendingUpdateRef.current.selected = selectedFeatures;

    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const pending = pendingUpdateRef.current;

        if (pending.main) {
          map.getSource(id)?.setData({
            type: 'FeatureCollection',
            features: pending.main,
          });
        }
        if (pending.selected) {
          map.getSource(selected)?.setData({
            type: 'FeatureCollection',
            features: pending.selected,
          });
        }
      });
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [id, selected, mainFeatures, selectedFeatures]);

  // Refs para rastrear valores anteriores e evitar chamadas desnecessárias ao MapLibre
  const prevShowDevicesRef = useRef(showDevices);
  const prevShowLabelsRef = useRef(showLabels);

  // Update layer visibility based on control panel settings
  // Otimizado: só chama setLayoutProperty se o valor realmente mudou
  useEffect(() => {
    if (!map) return;

    const devicesChanged = prevShowDevicesRef.current !== showDevices;
    const labelsChanged = prevShowLabelsRef.current !== showLabels;

    // Se nada mudou, não faz nada
    if (!devicesChanged && !labelsChanged) return;

    try {
      // Atualiza visibilidade dos dispositivos (se mudou)
      if (devicesChanged) {
        const visibility = showDevices ? 'visible' : 'none';
        [id, selected].forEach((source) => {
          if (map.getLayer(source)) {
            map.setLayoutProperty(source, 'visibility', visibility);
          }
        });
        if (map.getLayer(clusters)) {
          map.setLayoutProperty(clusters, 'visibility', visibility);
        }
        prevShowDevicesRef.current = showDevices;
      }

      // Atualiza labels (se mudou)
      if (labelsChanged) {
        const textField = showLabels ? `{${titleField || 'name'}}` : '';
        [id, selected].forEach((source) => {
          if (map.getLayer(source)) {
            map.setLayoutProperty(source, 'text-field', textField);
          }
        });
        prevShowLabelsRef.current = showLabels;
      }
    } catch {
      // Layers not yet created
    }
  }, [id, selected, clusters, showDevices, showLabels, titleField]);

  // Renderiza pulse animation para clusters (estilo GPSWOX)
  return (
    <MapClusterPulse
      mapReady
      sourceId={id}
      mapCluster={mapCluster}
    />
  );
};

// 2ECOMP: NÃO usar React.memo neste componente!
// React.memo bloqueia atualizações em tempo real dos marcadores no mapa.
// Ver: docs/PERFORMANCE-AUDIT-2ECOMP.md seção 9.1
export default MapPositions;
